import { Card } from '../../components/ui/Card'
import { Switch } from '../../components/ui/Switch'

/**
 * Cart "Separate POs per group" card (design: `Fictiv Platform.dc.html` ~L2313,
 * `splitAvail`) — a summary-rail toggle that, when on, previews the per-group
 * PO breakdown. Shown only when the cart splits into 2+ groups.
 *
 * STUB (UID-310, FE-only): the design places this on the CART, but the build's
 * real split-PO placement/state lives at CHECKOUT (UID-97 / ADR-0044,
 * `checkout-domain.ts: partitionReadyLinesIntoPoGroups` → POST /orders/batch).
 * This renders the design's cart preview from stubbed groups; wiring it to the
 * real grouping AND carrying the toggle choice through to checkout is the
 * follow-up.
 */
export interface CartPoGroup {
  readonly id: string
  /** "PO 1", "PO 2", … */
  readonly poLabel: string
  /** Group name, e.g. "Lower enclosure — rev 2" or "RFQ · Manifold End Cap". */
  readonly name: string
  /** Formatted amount, e.g. "$222.75". */
  readonly amountLabel: string
}

export interface CartSplitPoCardProps {
  readonly groups: readonly CartPoGroup[]
  readonly enabled: boolean
  readonly onToggle: (enabled: boolean) => void
  /** True while ANY cart mutation is in flight elsewhere on the page (a line
   * quantity/config edit, a service removal, a ship-plan/delivery-option
   * change, or a ship-to save) — disables the toggle so it can't fire
   * against a cart snapshot that's still mid-recalculation server-side. */
  readonly disabled?: boolean
}

export function CartSplitPoCard({
  groups,
  enabled,
  onToggle,
  disabled = false,
}: CartSplitPoCardProps) {
  // Split is only offered when the cart actually has 2+ groups (design: splitAvail).
  if (groups.length < 2) return null

  return (
    <Card className="cart-split-po-card">
      <div className="cart-split-po-row">
        <div className="cart-split-po-copy">
          <span className="cart-split-po-title">Separate POs per group</span>
          <span className="cart-split-po-subtitle">
            Place each session as its own purchase order — {groups.length} groups in this cart.
          </span>
        </div>
        <Switch
          id="cart-split-po-toggle"
          label="Separate POs per group"
          hideLabel
          checked={enabled}
          onChange={onToggle}
          disabled={disabled}
        />
      </div>
      {enabled && (
        <div className="cart-split-po-breakdown">
          {groups.map((group) => (
            <div key={group.id} className="cart-split-po-line">
              <span className="cart-split-po-line-label" title={`${group.poLabel} · ${group.name}`}>
                <b className="cart-split-po-num">{group.poLabel}</b> · {group.name}
              </span>
              <span className="cart-split-po-amount">{group.amountLabel}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
